import { useState, useRef } from "react";
import { toast } from "sonner";
import { Film, Upload, Link as LinkIcon, Loader2, Youtube } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * VideoInsertButton — bouton "Insérer une vidéo" réutilisable.
 * Insère `[video]URL[/video]` à la position du curseur d'un <textarea> cible.
 * Props :
 *  - targetRef : ref React vers le <textarea> cible
 *  - value : valeur actuelle du textarea
 *  - onChange : callback (nouvelle valeur) => void
 *  - label : label du contexte (ex: "FR", "NL", "Description")
 *  - testid : suffixe testid ex "fr" ou "vehicle-desc-fr"
 */
export default function VideoInsertButton({ targetRef, value, onChange, label = "", testid = "" }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const insert = (rawUrl) => {
    const current = value || "";
    const insertion = `\n\n[video]${rawUrl}[/video]\n\n`;
    const ta = targetRef?.current;
    let newValue;
    if (ta) {
      const start = ta.selectionStart ?? current.length;
      const end = ta.selectionEnd ?? current.length;
      newValue = current.slice(0, start) + insertion + current.slice(end);
      setTimeout(() => {
        ta.focus();
        const pos = start + insertion.length;
        ta.setSelectionRange(pos, pos);
      }, 30);
    } else {
      newValue = current + insertion;
    }
    onChange(newValue);
    setOpen(false);
    setUrl("");
    toast.success("Vidéo insérée");
  };

  const confirmUrl = () => {
    const t = url.trim();
    if (!t) { toast.error("URL requise"); return; }
    insert(t);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Fichier trop lourd (max 200 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insert(r.data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`insert-video-${testid}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest bg-[#C8A75A]/10 border border-[#C8A75A]/40 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black transition-colors px-3 py-2 rounded-md"
      >
        <Film className="w-3.5 h-3.5" />
        Insérer une vidéo{label ? ` · ${label}` : ""}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Insérer une vidéo{label ? ` · ${label}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2 mb-2">
                <LinkIcon className="w-3 h-3" /> Coller une URL YouTube ou Vimeo
              </label>
              <div className="flex gap-2">
                <Input
                  data-testid={`video-url-input-${testid}`}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://youtu.be/... ou https://vimeo.com/..."
                  className="bg-[#0D0D0D] border-white/10"
                />
                <Button
                  data-testid={`video-url-insert-${testid}`}
                  onClick={confirmUrl}
                  className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest"
                >
                  Insérer
                </Button>
              </div>
              <p className="text-white/40 text-xs mt-2 flex items-center gap-2">
                <Youtube className="w-3 h-3 text-red-400" /> YouTube, Vimeo, ou MP4 direct
              </p>
            </div>
            <div className="border-t border-white/10 pt-4">
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2 mb-2">
                <Upload className="w-3 h-3" /> Ou téléverser un fichier MP4
              </label>
              <label
                data-testid={`video-upload-label-${testid}`}
                className={`inline-flex items-center gap-2 bg-black/40 border border-white/10 hover:border-[#C8A75A]/50 rounded-md px-4 py-3 cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-[#C8A75A]" />}
                <span className="text-sm">{uploading ? "Envoi…" : "Choisir un fichier vidéo (MP4, max 200 Mo)"}</span>
                <input
                  ref={fileInputRef}
                  data-testid={`video-upload-input-${testid}`}
                  type="file"
                  accept="video/mp4,video/webm,video/*"
                  onChange={uploadFile}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
